/**
 * Checklists Dashboard Component
 *
 * Trello-style board showing checklists by status.
 */

import React, { useState, useEffect, useCallback } from "react";
import {
  useOsNavigate,
  useWindowId,
  useOsActionsOptional,
  useIsOsMode,
} from "@/components/ui/os-layout";
import { OpsSubpageHeadingClassic } from "@/components/ops/OpsSubpageHeadingClassic";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faClipboardCheck,
  faRefresh,
  faCircleNotch,
  faPlay,
  faCheck,
  faExclamationTriangle,
  faHistory,
  faGears,
  faHourglass,
  faUserCheck,
  faCircleXmark,
} from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import { Badge } from "@/components/ui/badge/Badge";
import { useNotification } from "@/context/NotificationContext";
import {
  getTodayDashboard,
  startRun,
  type TodayDashboardResponse,
  type TodayChecklistSummary,
  OPS_TEAM_MEMBERS,
} from "@/services/api/opsChecklists";
import { ChecklistBulletPoints } from "./ChecklistBulletPoints";

type BoardColumn = "not_started" | "in_progress" | "blocked" | "awaiting_review" | "done";

interface ColumnConfig {
  id: BoardColumn;
  title: string;
  icon: typeof faHourglass;
  color: string;
  bgColor: string;
}

const COLUMNS: ColumnConfig[] = [
  {
    id: "not_started",
    title: "Not Started",
    icon: faHourglass,
    color: "text-slate-500",
    bgColor: "bg-slate-100 dark:bg-slate-800/50",
  },
  {
    id: "in_progress",
    title: "In Progress",
    icon: faPlay,
    color: "text-indigo-500",
    bgColor: "bg-indigo-50 dark:bg-indigo-900/20",
  },
  {
    id: "blocked",
    title: "Blocked (RED)",
    icon: faCircleXmark,
    color: "text-red-500",
    bgColor: "bg-red-50 dark:bg-red-900/20",
  },
  {
    id: "awaiting_review",
    title: "Awaiting 2nd Check",
    icon: faUserCheck,
    color: "text-amber-500",
    bgColor: "bg-amber-50 dark:bg-amber-900/20",
  },
  {
    id: "done",
    title: "Done",
    icon: faCheck,
    color: "text-green-500",
    bgColor: "bg-green-50 dark:bg-green-900/20",
  },
];

function getColumn(summary: TodayChecklistSummary): BoardColumn {
  if (!summary.run) return "not_started";
  
  if (summary.run.status === "approved") return "done";
  if (summary.run.status === "awaiting_review") return "awaiting_review";
  if (summary.run.status === "in_progress" || summary.run.status === "rejected") {
    // Check if blocked (has RED steps)
    if (summary.has_red_steps) return "blocked";
    return "in_progress";
  }
  
  return "not_started";
}

interface StartRunDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onStart: (userName: string) => void;
  checklistName: string;
  defaultAssignee?: string;
  isLoading: boolean;
}

function StartRunDialog({
  isOpen,
  onClose,
  onStart,
  checklistName,
  defaultAssignee,
  isLoading,
}: StartRunDialogProps) {
  const [selectedUser, setSelectedUser] = useState<string>(defaultAssignee || "");

  // Reset to default when dialog opens with new checklist
  useEffect(() => {
    if (isOpen) {
      setSelectedUser(defaultAssignee || "");
    }
  }, [isOpen, defaultAssignee]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-slate-800 rounded-xl p-6 max-w-md w-full mx-4 shadow-xl">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
          Start Checklist
        </h3>
        <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
          Starting: <strong>{checklistName}</strong>
        </p>

        <div className="mb-6">
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
            Who are you?
          </label>
          <select
            value={selectedUser}
            onChange={(e) => setSelectedUser(e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          >
            <option value="">Select your name...</option>
            {OPS_TEAM_MEMBERS.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </div>

        <div className="flex gap-3 justify-end">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => onStart(selectedUser)}
            disabled={!selectedUser || isLoading}
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isLoading && (
              <FontAwesomeIcon icon={faCircleNotch} className="animate-spin" />
            )}
            Start Checklist
          </button>
        </div>
      </div>
    </div>
  );
}

interface ChecklistCardProps {
  summary: TodayChecklistSummary;
  onStartRun: (checklistId: string, checklistName: string, defaultAssignee?: string) => void;
}

function ChecklistCard({ summary, onStartRun }: ChecklistCardProps) {
  const osNavigate = useOsNavigate();
  const column = getColumn(summary);
  const hasRun = !!summary.run;

  // Check if overdue (past deadline and not done)
  const isOverdue = (() => {
    if (!summary.deadline) return false;
    if (summary.run?.status === "approved") return false;
    
    const now = new Date();
    const [hours, minutes] = summary.deadline.split(":").map(Number);
    const deadlineTime = new Date();
    deadlineTime.setHours(hours, minutes, 0, 0);
    
    return now > deadlineTime;
  })();

  const handleClick = () => {
    if (!hasRun) {
      onStartRun(summary.checklist_id, summary.checklist_name, summary.default_assignee);
    } else if (summary.run?.status === "awaiting_review") {
      osNavigate(
        `/ops/checklists/review/${summary.run.run_id}`,
        `Review: ${summary.checklist_name}`,
      );
    } else if (summary.run?.status === "approved") {
      osNavigate(
        `/ops/checklists/run/${summary.run.run_id}`,
        summary.checklist_name,
      );
    } else {
      // in_progress or rejected
      osNavigate(
        `/ops/checklists/run/${summary.run?.run_id}`,
        summary.checklist_name,
      );
    }
  };

  const getActionLabel = () => {
    if (!hasRun) return "Start";
    if (summary.run?.status === "rejected") return "Fix & Resubmit";
    if (summary.run?.status === "awaiting_review") return "Review";
    if (summary.run?.status === "approved") return "View";
    if (summary.has_red_steps) return "Resolve Issues";
    return "Continue";
  };

  return (
    <div
      onClick={handleClick}
      className={`rounded-lg border p-3 cursor-pointer hover:shadow-md transition-all ${
        isOverdue
          ? "bg-red-50 dark:bg-red-900/20 border-red-300 dark:border-red-700 hover:border-red-400 dark:hover:border-red-600"
          : "bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600"
      }`}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className={`font-medium text-sm ${isOverdue ? "text-red-700 dark:text-red-300" : "text-slate-900 dark:text-white"}`}>
          {summary.checklist_name}
        </div>
        {summary.deadline && (
          <span className={`text-xs whitespace-nowrap ${
            isOverdue ? "text-red-600 dark:text-red-400 font-medium" : "text-slate-500 dark:text-slate-400"
          }`}>
            {isOverdue ? "Overdue" : `Due ${summary.deadline}`}
          </span>
        )}
      </div>

      {/* Default assignee (when not started) */}
      {!hasRun && summary.default_assignee && (
        <div className="text-xs text-slate-500 dark:text-slate-400 mb-2">
          Assigned to {summary.default_assignee}
        </div>
      )}

      <ChecklistBulletPoints
        items={summary.bullet_points}
        compact
        className="mb-2"
      />

      {/* Progress bar */}
      {hasRun && (
        <div className="flex items-center gap-2 mb-2">
          <div className="flex-1 h-1.5 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
            <div
              className={`h-full transition-all duration-300 ${
                summary.has_red_steps
                  ? "bg-red-500"
                  : summary.has_amber_steps
                    ? "bg-amber-500"
                    : "bg-green-500"
              }`}
              style={{
                width: `${(summary.completed_steps / summary.total_steps) * 100}%`,
              }}
            />
          </div>
          <span className="text-xs text-slate-500 dark:text-slate-400">
            {summary.completed_steps}/{summary.total_steps}
          </span>
        </div>
      )}

      {/* Meta info */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {hasRun && (
            <span className="text-xs text-slate-500 dark:text-slate-400">
              {summary.run?.status === "awaiting_review" && summary.run?.second_checker
                ? summary.run.second_checker
                : summary.run?.primary_user}
            </span>
          )}
          {summary.run?.status === "rejected" && (
            <Badge color="red" className="text-xs">Rejected</Badge>
          )}
        </div>
        <span className={`text-xs font-medium ${isOverdue ? "text-red-600 dark:text-red-400" : "text-indigo-600 dark:text-indigo-400"}`}>
          {getActionLabel()} →
        </span>
      </div>
    </div>
  );
}

interface BoardColumnProps {
  config: ColumnConfig;
  checklists: TodayChecklistSummary[];
  onStartRun: (checklistId: string, checklistName: string, defaultAssignee?: string) => void;
}

function BoardColumnComponent({ config, checklists, onStartRun }: BoardColumnProps) {
  return (
    <div className={`flex flex-col rounded-xl ${config.bgColor} min-w-[280px] max-w-[320px] flex-1`}>
      {/* Column header */}
      <div className="p-3 border-b border-slate-200/50 dark:border-slate-700/50">
        <div className="flex items-center gap-2">
          <FontAwesomeIcon icon={config.icon} className={`text-sm ${config.color}`} />
          <span className="font-medium text-slate-900 dark:text-white text-sm">
            {config.title}
          </span>
          <span className="ml-auto text-xs text-slate-500 bg-white dark:bg-slate-700 px-2 py-0.5 rounded-full">
            {checklists.length}
          </span>
        </div>
      </div>

      {/* Cards */}
      <div className="flex-1 p-2 space-y-2 overflow-auto">
        {checklists.length === 0 ? (
          <div className="text-center py-4 text-xs text-slate-400">
            No items
          </div>
        ) : (
          checklists.map((summary) => (
            <ChecklistCard
              key={summary.checklist_id}
              summary={summary}
              onStartRun={onStartRun}
            />
          ))
        )}
      </div>
    </div>
  );
}

export function ChecklistsDashboard() {
  const isOsMode = useIsOsMode();
  const { addNotification } = useNotification();
  const osNavigate = useOsNavigate();
  const windowId = useWindowId();
  const osActions = useOsActionsOptional();

  /**
   * Navigate to history / config / issues inside the same aniOS window when possible,
   * so window `params` update (plain `Link` only changes the URL and does not switch views).
   */
  const navigateChecklistsSubview = useCallback(
    (
      classicPath: string,
      patch: { params: Record<string, string>; title: string },
    ) => {
      if (windowId && osActions) {
        osActions.updateWindow(windowId, patch);
        return;
      }
      osNavigate(classicPath, patch.title);
    },
    [windowId, osActions, osNavigate],
  );

  const [dashboard, setDashboard] = useState<TodayDashboardResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Start run dialog
  const [showStartDialog, setShowStartDialog] = useState(false);
  const [startingChecklist, setStartingChecklist] = useState<{
    id: string;
    name: string;
    defaultAssignee?: string;
  } | null>(null);
  const [isStartingRun, setIsStartingRun] = useState(false);

  const loadDashboard = useCallback(async (showRefreshState = false) => {
    try {
      if (showRefreshState) setIsRefreshing(true);
      const data = await getTodayDashboard();
      setDashboard(data);
    } catch (error) {
      console.error("Failed to load dashboard:", error);
      addNotification({
        type: "error",
        message: "Failed to load checklists",
      });
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [addNotification]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const handleStartRun = (checklistId: string, checklistName: string, defaultAssignee?: string) => {
    setStartingChecklist({ id: checklistId, name: checklistName, defaultAssignee });
    setShowStartDialog(true);
  };

  const handleConfirmStart = async (userName: string) => {
    if (!startingChecklist) return;

    setIsStartingRun(true);
    try {
      const run = await startRun(startingChecklist.id, { user_name: userName });
      setShowStartDialog(false);
      setStartingChecklist(null);
      osNavigate(`/ops/checklists/run/${run.run_id}`, startingChecklist.name);
    } catch (error) {
      console.error("Failed to start run:", error);
      addNotification({
        type: "error",
        message: "Failed to start checklist",
      });
    } finally {
      setIsStartingRun(false);
    }
  };

  // Group checklists by column
  const groupedByColumn = COLUMNS.reduce(
    (acc, col) => {
      acc[col.id] = [];
      return acc;
    },
    {} as Record<BoardColumn, TodayChecklistSummary[]>
  );

  dashboard?.checklists.forEach((summary) => {
    const column = getColumn(summary);
    groupedByColumn[column].push(summary);
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <FontAwesomeIcon
          icon={faCircleNotch}
          className="text-2xl text-indigo-500 animate-spin"
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0 p-6">
      {/* Header — OS: window title is enough; no duplicate back/title (date kept as context) */}
      <div className="flex items-center justify-between mb-6 flex-shrink-0">
        <OpsSubpageHeadingClassic
          title="Ops Checklists"
          titleClassName="text-xl font-bold text-slate-900 dark:text-white"
          useOsLink
          hubWindowTitle="Operations"
          linkClassName="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors inline-flex"
          beforeTitle={
            !isOsMode ? (
              <FontAwesomeIcon
                icon={faClipboardCheck}
                className="text-indigo-500 text-xl"
              />
            ) : undefined
          }
          subtitle={
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {dashboard?.date || "Today"}
            </p>
          }
        />

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() =>
              navigateChecklistsSubview("/ops/checklists/issues", {
                params: { checklistView: "issues" },
                title: "Checklist Issues",
              })
            }
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-amber-700 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded-lg transition-colors"
          >
            <FontAwesomeIcon icon={faExclamationTriangle} />
            Issues
          </button>
          <button
            type="button"
            onClick={() =>
              navigateChecklistsSubview("/ops/checklists/history", {
                params: { checklistView: "history" },
                title: "Checklist History",
              })
            }
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
          >
            <FontAwesomeIcon icon={faHistory} />
            History
          </button>
          <button
            type="button"
            onClick={() =>
              navigateChecklistsSubview("/ops/checklists/config", {
                params: { checklistView: "config" },
                title: "Checklist Config",
              })
            }
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
          >
            <FontAwesomeIcon icon={faGears} />
            Configure
          </button>
          <button
            onClick={() => loadDashboard(true)}
            disabled={isRefreshing}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors disabled:opacity-50"
          >
            <FontAwesomeIcon
              icon={isRefreshing ? faCircleNotch : faRefresh}
              className={isRefreshing ? "animate-spin" : ""}
            />
            Refresh
          </button>
        </div>
      </div>

      {/* Board */}
      <div className="flex-1 min-h-0 overflow-x-auto">
        {dashboard?.checklists.length === 0 ? (
          <div className="text-center py-12">
            <FontAwesomeIcon
              icon={faClipboardCheck}
              className="text-4xl text-slate-300 dark:text-slate-600 mb-3"
            />
            <p className="text-slate-500 dark:text-slate-400">
              No checklists configured yet.
            </p>
            <button
              type="button"
              data-testid="checklists-empty-create-config"
              onClick={() =>
                navigateChecklistsSubview("/ops/checklists/config", {
                  params: { checklistView: "config" },
                  title: "Checklist Config",
                })
              }
              className="text-indigo-600 dark:text-indigo-400 hover:underline text-sm mt-2 inline-block"
            >
              Create your first checklist
            </button>
          </div>
        ) : (
          <div className="flex gap-4 h-full pb-4">
            {COLUMNS.map((column) => (
              <BoardColumnComponent
                key={column.id}
                config={column}
                checklists={groupedByColumn[column.id]}
                onStartRun={handleStartRun}
              />
            ))}
          </div>
        )}
      </div>

      {/* Start Run Dialog */}
      <StartRunDialog
        isOpen={showStartDialog}
        onClose={() => {
          setShowStartDialog(false);
          setStartingChecklist(null);
        }}
        onStart={handleConfirmStart}
        checklistName={startingChecklist?.name || ""}
        defaultAssignee={startingChecklist?.defaultAssignee}
        isLoading={isStartingRun}
      />
    </div>
  );
}
